import { useEffect, useState } from "react";
import { GitMerge } from "lucide-react";
import api from "../../lib/api";
import ScorecardInfoTip from "./ScorecardInfoTip";
import MoMDeltaPill from "./MoMDeltaPill";

/**
 * Marketing Attribution Rate — Scorecard KPI (Romantiezer · 25 %).
 *
 * Composite of Digital Attribution Completeness (40 %) + CRM
 * Attribution Completeness (30 %) + Cross-source Match Rate (30 %).
 *
 * Data source: `GET /api/scorecard/attribution` — cached 90s.
 */
const fmtInt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;

const GRADE_META = {
  excellent: { label: "Excelente", color: "#16a34a" },
  good:      { label: "Bueno",     color: "#0ea5e9" },
  warning:   { label: "Alerta",    color: "#f97316" },
  poor:      { label: "Crítico",   color: "#dc2626" },
};

export default function AttributionRateCard({ period, dateRange, businessUnit }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = { period };
        if (period === "custom" && dateRange?.start && dateRange?.end) {
          params.start_date = dateRange.start;
          params.end_date   = dateRange.end;
        }
        if (businessUnit) params.business_unit = businessUnit;
        const r = await api.get("/scorecard/attribution", { params });
        if (mounted) setData(r.data);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || e.message);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [period, dateRange?.start, dateRange?.end, businessUnit]);

  if (loading && !data) {
    return (
      <section className="surface p-4" data-testid="attribution-rate-loading">
        <div className="animate-pulse bg-slate-100" style={{ height: 180, borderRadius: 8 }}/>
      </section>
    );
  }
  if (error) {
    return (
      <section className="surface p-4" data-testid="attribution-rate-error">
        <div className="text-xs font-bold text-red-600">Error Attribution: {error}</div>
      </section>
    );
  }
  if (!data) return null;

  const score = data.attribution_rate || 0;
  const grade = GRADE_META[data.grade] || GRADE_META.warning;
  const c     = data.components;

  const bars = [
    { key: "digital_completeness", label: "Digital",  weight: "40%",
      pct: c.digital_completeness.pct,
      sub: `${fmtInt(c.digital_completeness.full)} / ${fmtInt(c.digital_completeness.total)} con UTM · ${fmtInt(c.digital_completeness.direct_excluded || 0)} directos excluidos`,
      tint: "#0ea5e9" },
    { key: "crm_completeness",     label: "CRM",      weight: "30%",
      pct: c.crm_completeness.pct,
      sub: `${fmtInt(c.crm_completeness.full)} / ${fmtInt(c.crm_completeness.total)} con origen + campaña`,
      tint: "#8b5cf6" },
    { key: "cross_source_match",   label: "Match Cross-source", weight: "30%",
      pct: c.cross_source_match.pct,
      sub: `${fmtInt(c.cross_source_match.matched)} / ${fmtInt(c.cross_source_match.crm_total)} matcheados`,
      tint: "#14b8a6" },
  ];

  return (
    <section className="surface p-4" data-testid="attribution-rate-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="kpi-icon-clean" style={{
          width: 32, height: 32, color: "#f59e0b",
          boxShadow: "inset 0 0 0 1px #f59e0b33",
        }}>
          <GitMerge size={16}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1">
            Marketing Attribution Rate
            <ScorecardInfoTip
              label="Marketing Attribution Rate"
              formula="Composite 0-100: Digital UTM 40% + CRM Origen+Campaña 30% + Match Cross-source 30%"
              extra={`Romantiezer · Scorecard Jul 2026 · 25% del peso individual. Mide cuánto podemos atribuir el revenue a un canal/campaña específico. Score cacheado 90s.`}
              source={`leads_digital + leads_raw · ${data.period.start} → ${data.period.end}`}
            />
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            {data.period.start} → {data.period.end} · {data.business_unit}
          </div>
        </div>
      </div>

      {/* Score headline */}
      <div className="flex items-baseline gap-2 mb-3"
        style={{ fontFamily: "Manrope, sans-serif" }}>
        <span className="font-extrabold tabular-nums"
          style={{ fontSize: 40, color: grade.color, letterSpacing: "-0.03em", lineHeight: 1 }}>
          {score.toFixed(1)}
        </span>
        <span className="text-sm font-bold text-gray-500">/ 100</span>
        <MoMDeltaPill metric="attribution" />
        <span className="ml-auto text-[10px] font-bold uppercase tabular-nums px-2 py-0.5 rounded"
          style={{ background: `${grade.color}18`, color: grade.color }}>
          {grade.label}
        </span>
      </div>

      {/* Component bars */}
      <div className="space-y-2">
        {bars.map((b) => (
          <div key={b.key} data-testid={`attribution-comp-${b.key}`}>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-bold text-gray-700">{b.label}</span>
                <span className="text-[9px] font-semibold text-gray-400">· {b.weight}</span>
              </div>
              <span className="tabular-nums font-extrabold text-[11px]" style={{ color: b.tint }}>
                {fmtPct(b.pct)}
              </span>
            </div>
            <div style={{ height: 6, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width:  `${Math.min(100, b.pct)}%`,
                background: b.tint,
                transition: "width 0.4s ease",
              }}/>
            </div>
            <div className="text-[9px] text-gray-500 mt-0.5">{b.sub}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
